import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { extractErrorMessage, useAddFollower, useRemoveFollower } from '@/queries/ticketMutations';
import { useUserSearch } from '@/queries/people';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SearchResults } from './SearchResults';
import type { Ticket } from '@/types/ticket';

// Staff rail: customers besides the requester who can see and reply to the
// ticket. Only rendered on the admin's own unlensed route (TicketDetailView
// gates it on the requester block), which is the detail cache slot the
// follower mutations write to.
export function TicketFollowers({ ticket }: { ticket: Ticket }) {
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const debounced = useDebouncedValue(query);
  const search = useUserSearch(debounced, ticket.companyId);
  const add = useAddFollower(ticket._id);
  const remove = useRemoveFollower(ticket._id);

  const followers = ticket.followerUsers ?? [];
  const following = new Set([...(ticket.followers ?? []), ...followers.map(f => f._id)]);
  // The API refuses staff, the requester and duplicates, so they are never offered.
  const candidates = search.data?.filter(u => !u.isAdmin && u._id !== ticket.createdBy && !following.has(u._id));

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(extractErrorMessage(err, fallback));
    }
  };

  return (
    <section data-testid="ticket-followers" aria-label="People on this ticket" className="mb-5 border-b border-etn-border-hairline pb-5">
      <h2 className="text-etn-micro text-etn-text-label">People on this ticket</h2>
      <p className="mt-0.5 text-etn-micro text-etn-text-soft">People added here can see and reply to this ticket, and get its emails.</p>

      {followers.length > 0 && (
        <ul className="mt-2.5 space-y-1.5">
          {followers.map(f => (
            <li key={f._id} className="flex items-center justify-between gap-2">
              <div className="min-w-0 text-etn-control">
                <div className="truncate text-etn-text-cell">{f.name || f.email || f._id}</div>
                {f.name && f.email && <div className="truncate text-etn-micro text-etn-text-soft">{f.email}</div>}
              </div>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${f.name || f.email || 'person'}`}
                disabled={remove.isPending}
                onClick={() => run(() => remove.mutateAsync(f._id), 'Could not remove that person')}
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2.5">
        <Input
          aria-label="Add person"
          placeholder="Add person"
          autoComplete="off"
          value={query}
          disabled={add.isPending}
          onChange={e => setQuery(e.target.value)}
        />
        <SearchResults
          label="People to add"
          term={query}
          items={candidates}
          isFetching={search.isFetching || query !== debounced}
          onPick={user =>
            run(async () => {
              await add.mutateAsync(user._id);
              setQuery('');
            }, 'Could not add that person')
          }
          renderItem={user => (
            <>
              <div className="truncate text-etn-text-cell">{user.name || user.email || user._id}</div>
              {user.name && user.email && <div className="truncate text-etn-micro text-etn-text-soft">{user.email}</div>}
            </>
          )}
        />
      </div>

      {error && (
        <p role="alert" className="mt-1.5 text-etn-micro text-etn-danger-text">
          {error}
        </p>
      )}
    </section>
  );
}
